import {
    bookmarkForZodUnion,
    bookmarkOwnerTypeZodLiteral,
    bookmarkOwnerZodUnion,
    bookmarkScopeZodArray,
} from "@@/shared/bookmarks/bookmark";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import {
    isProviderId,
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";

const setBookmarkCommandZodObject = z
    .object({
        _id: idZodSchema,
        _type: z.literal("SET_BOOKMARK_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        /**
         * Does the bookmarked item belong to a specific provider?
         * If so, this should be set to the providerId it belongs to.
         */
        providerId: providerIdZodSchema.optional(),
        /** The id of the bookmarked item */
        bookmarkedId: bookmarkForZodUnion,
        /** marked or unmarked? */
        marked: z.boolean(),
        /** The type of owner, user or provider */
        ownerType: bookmarkOwnerTypeZodLiteral,
        /**
         * Who is the bookmark visible for?
         * What user or, if provider,
         * all users belonging to that provider. \
         * NOTE: If set to a user id, it will fail if
         * the user that is requesting the bookmark
         * does not have access to connect the
         * bookmark to that user
         */
        ownerId: bookmarkOwnerZodUnion,
        // Scope is used to filter bookmarks for a specific
        // part of the app, e.g. if set to a providerId
        // the bookmark is for the given providers back office
        scope: bookmarkScopeZodArray,
    })
    .refine(value => {
        if (value.ownerType === "provider" && !isProviderId(value.providerId))
            return false;
        return true;
    });

type SetBookmarkCommand = z.infer<typeof setBookmarkCommandZodObject>;

type FactoryParams = Except<SetBookmarkCommand, "_id" | "_type" | "_createdAt">;

const setBookmarkCommandFactory = (params: FactoryParams) => {
    const command: SetBookmarkCommand = {
        ...params,
        _id: generateId({ prefix: "set_bookmark_command__" }),
        _type: "SET_BOOKMARK_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

export { setBookmarkCommandFactory, setBookmarkCommandZodObject };
export type { SetBookmarkCommand };
